import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Brush, 
  Palette, 
  Type, 
  Square, 
  Layout, 
  Plus, 
  Save, 
  Trash2, 
  ChevronRight,
  Monitor,
  Tablet,
  Smartphone,
  Check,
  Zap,
  Undo2,
  Redo2,
  ArrowLeft,
  Image as ImageIcon,
  Type as TypeIcon,
  MousePointer2,
  Settings2,
  Maximize2,
  RefreshCw,
  Upload,
  Layers,
  X,
  History,
  Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy, 
  limit, 
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { toast } from 'sonner';
import { trackUserAction } from '../lib/events';
import { handleFirestoreError } from '../lib/firebaseUtils';
import { parseFiles } from '../lib/parser';

// --- Injection Script ---
const SELECTION_SCRIPT = `
(function() {
  let selectedEl = null;

  // Track selection state to avoid infinite loops if script re-injects
  if (window.__YACHT_EDITOR_INITIALIZED__) return;
  window.__YACHT_EDITOR_INITIALIZED__ = true;

  // Add styles for selection
  const style = document.createElement('style');
  style.textContent = \`
    .yacht-hover { outline: 2px solid #0074D9 !important; outline-offset: -2px !important; cursor: pointer !important; }
    .yacht-selected { outline: 2px solid #0074D9 !important; outline-offset: -2px !important; box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.1) !important; z-index: 9999 !important; position: relative !important; }
  \`;
  document.head.appendChild(style);

  document.body.addEventListener('mouseover', (e) => {
    e.stopPropagation();
    if (e.target === document.body || e.target === document.documentElement) return;
    e.target.classList.add('yacht-hover');
  }, true);

  document.body.addEventListener('mouseout', (e) => {
    e.target.classList.remove('yacht-hover');
  }, true);

  document.body.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedEl) selectedEl.classList.remove('yacht-selected');
    selectedEl = e.target;
    selectedEl.classList.add('yacht-selected');
    
    const computedStyle = window.getComputedStyle(e.target);
    
    window.parent.postMessage({
      type: 'YACHT_ELEMENT_SELECTED',
      tagName: e.target.tagName,
      id: e.target.id,
      className: e.target.className,
      innerHTML: e.target.innerHTML,
      innerText: e.target.innerText,
      src: e.target.src,
      rect: e.target.getBoundingClientRect(),
      style: {
        color: computedStyle.color,
        fontSize: computedStyle.fontSize,
        backgroundColor: computedStyle.backgroundColor,
        borderRadius: computedStyle.borderRadius,
        padding: computedStyle.padding,
        margin: computedStyle.margin,
        fontFamily: computedStyle.fontFamily
      }
    }, '*');
  }, true);
  
  window.addEventListener('message', (e) => {
    if (e.data.type === 'YACHT_UPDATE_ELEMENT') {
      if (selectedEl) {
        const { content, src, styles } = e.data;
        if (content !== undefined) selectedEl.innerHTML = content;
        if (src !== undefined) selectedEl.src = src;
        if (styles) {
          Object.assign(selectedEl.style, styles);
        }
      }
    }
    if (e.data.type === 'YACHT_GET_HTML') {
      // Remove our classes before sending back
      const hoverEls = document.querySelectorAll('.yacht-hover');
      hoverEls.forEach(el => el.classList.remove('yacht-hover'));
      const selectedEls = document.querySelectorAll('.yacht-selected');
      selectedEls.forEach(el => el.classList.remove('yacht-selected'));
      
      window.parent.postMessage({
        type: 'YACHT_HTML_OUTPUT',
        html: document.documentElement.outerHTML
      }, '*');
    }
    
    if (e.data.type === 'YACHT_APPLY_GLOBAL_STYLES') {
      const { tokens, typography, density, componentStyle, colors, fonts } = e.data;
      
      // Update tokens (colors)
      const colorSource = tokens || colors;
      if (colorSource) {
        Object.entries(colorSource).forEach(([key, val]) => {
          document.documentElement.style.setProperty(\`--\${key}\`, val);
          if (key === 'background') document.body.style.backgroundColor = val;
          if (key === 'text') document.body.style.color = val;
        });

        // Inject Contrast Safety Layer
        let styleTag = document.getElementById('yacht-theme-overrides');
        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = 'yacht-theme-overrides';
          document.head.appendChild(styleTag);
        }

        styleTag.innerHTML = \`
          :root {
            --primary: \${colorSource.primary};
            --background: \${colorSource.background};
            --text: \${colorSource.text};
            --surface: \${colorSource.surface};
            --accent: \${colorSource.accent};
            --radius: \${componentStyle?.radius || '8px'};
          }
          
          /* Visibility Overrides for Contrast Safety */
          .text-white, .text-zinc-50, .text-slate-50, .text-gray-50 {
             color: var(--text) !important;
             filter: contrast(1.2);
          }
          
          .bg-white, .bg-zinc-50, .bg-slate-50, .bg-gray-50 {
             background-color: var(--surface) !important;
          }

          /* Global font scale and hierarchy */
          html { font-size: calc(16px * \${typography?.scale || '1'}); }
          
          /* Forced Brand Visibility */
          h1, .brand-text, header span { color: var(--text) !important; }

          /* Density specific adjustments */
          \${density === 'compact' ? \`
            .p-4, .p-5, .p-6, .px-6, .py-6 { padding: 0.75rem !important; }
            .gap-4, .gap-6 { gap: 0.5rem !important; }
          \` : ''}

          \${density === 'spacious' ? \`
            .p-4, .p-5, .p-6, .px-6, .py-6 { padding: 2rem !important; }
            .gap-2, .gap-3, .gap-4 { gap: 1.5rem !important; }
          \` : ''}
        \`;
      }

      // Update typography
      const fontSource = typography || fonts;
      if (fontSource) {
        if (fontSource.body) document.body.style.fontFamily = fontSource.body;
        if (fontSource.heading) {
          const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6, .heading');
          headings.forEach(h => h.style.fontFamily = fontSource.heading);
        }
      }

      // Component Specific Styles
      if (componentStyle) {
        const buttons = document.querySelectorAll('button, .btn, .button, input[type="submit"]');
        buttons.forEach(b => {
          if (componentStyle.radius) b.style.borderRadius = componentStyle.radius;
          if (componentStyle.shadow) b.style.boxShadow = componentStyle.shadow;
          if (componentStyle.border) b.style.border = componentStyle.border;
          
          if (componentStyle.style === 'outline') {
            b.style.backgroundColor = 'transparent';
            if (colorSource?.primary) b.style.borderColor = colorSource.primary;
          } else if (componentStyle.style === 'ghost') {
            b.style.backgroundColor = 'transparent';
            b.style.border = 'none';
          }
        });

        const surfaces = document.querySelectorAll('.card, [class*="card"], .bg-surface, article, section > div');
        surfaces.forEach(s => {
          if (componentStyle.radius) s.style.borderRadius = componentStyle.radius;
          if (componentStyle.shadow) s.style.boxShadow = componentStyle.shadow;
          if (componentStyle.border) s.style.border = componentStyle.border;
        });
      }
    }
  });
})();
`;

interface VisualElement {
  tagName: string;
  id: string;
  className: string;
  innerHTML: string;
  innerText: string;
  src?: string;
  style: any;
}

// --- Theme Selection & Design Intelligence ---

const DESIGN_RULES = {
  contrastThreshold: 128, // Midpoint for YIQ contrast calc
  safetyGutter: '2rem',
  minTouchTarget: '44px',
  accessibilityMode: true
};

const getContrastColor = (hexcolor: string) => {
  const r = parseInt(hexcolor.substring(1, 3), 16);
  const g = parseInt(hexcolor.substring(3, 5), 16);
  const b = parseInt(hexcolor.substring(5, 7), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= DESIGN_RULES.contrastThreshold) ? '#000000' : '#FFFFFF';
};

// --- Theme Definitions ---
const THEMES = {
  'modern-ai': {
    name: 'Modern AI',
    description: 'Dark, glowing, and futuristic. Optimized for intelligent agents.',
    tokens: {
      primary: '#00F2FF',
      background: '#09090B',
      text: '#FAFAFA',
      surface: '#121214',
      accent: '#8B5CF6',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444'
    },
    typography: {
      heading: 'Space Grotesk',
      body: 'Inter',
      scale: '1.1'
    },
    density: 'spacious' as const,
    componentStyle: {
      radius: '16px',
      shadow: '0 0 20px rgba(0, 242, 255, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      style: 'filled' as const
    }
  },
  'workhorse': {
    name: 'Workhorse',
    description: 'Light, dense, and dependable. Built for heavy data manipulation.',
    tokens: {
      primary: '#2563EB',
      background: '#F8FAFC',
      text: '#0F172A',
      surface: '#FFFFFF',
      accent: '#475569',
      success: '#16A34A',
      warning: '#B45309',
      error: '#DC2626'
    },
    typography: {
      heading: 'Inter',
      body: 'Inter',
      scale: '0.9'
    },
    density: 'compact' as const,
    componentStyle: {
      radius: '6px',
      shadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #E2E8F0',
      style: 'filled' as const
    }
  },
  'brutalist': {
    name: 'Brutalist',
    description: 'Raw high-contrast mono. No shadows, bold boundaries.',
    tokens: {
      primary: '#000000',
      background: '#FFFFFF',
      text: '#000000',
      surface: '#FFFFFF',
      accent: '#FFFF00',
      success: '#000000',
      warning: '#000000',
      error: '#FF0000'
    },
    typography: {
      heading: 'JetBrains Mono',
      body: 'JetBrains Mono',
      scale: '1'
    },
    density: 'medium' as const,
    componentStyle: {
      radius: '0px',
      shadow: 'none',
      border: '3px solid #000000',
      style: 'filled' as const
    }
  },
  'editorial': {
    name: 'Editorial',
    description: 'Serif-driven, airy, and elegant. Priority on reading experience.',
    tokens: {
      primary: '#1A1A1A',
      background: '#FCFBF7',
      text: '#1C1C1C',
      surface: '#FFFFFF',
      accent: '#6B4F4F',
      success: '#4A6741',
      warning: '#C28E40',
      error: '#A34D4D'
    },
    typography: {
      heading: 'Playfair Display',
      body: 'PT Serif',
      scale: '1.2'
    },
    density: 'spacious' as const,
    componentStyle: {
      radius: '0px',
      shadow: 'none',
      border: 'none',
      style: 'ghost' as const
    }
  },
  'playful': {
    name: 'Playful',
    description: 'Vibrant, soft, and expressive. Friendly interactions first.',
    tokens: {
      primary: '#FF4D94',
      background: '#FFF9FB',
      text: '#3D1A2B',
      surface: '#FFFFFF',
      accent: '#7C3AED',
      success: '#059669',
      warning: '#D97706',
      error: '#EF4444'
    },
    typography: {
      heading: 'Outfit',
      body: 'Outfit',
      scale: '1.05'
    },
    density: 'medium' as const,
    componentStyle: {
      radius: '24px',
      shadow: '0 8px 30px rgba(255, 77, 148, 0.12)',
      border: 'none',
      style: 'filled' as const
    }
  },
  'enterprise': {
    name: 'Enterprise',
    description: 'Neutral, grid-based, and scalable. Safe for scale.',
    tokens: {
      primary: '#0F172A',
      background: '#FFFFFF',
      text: '#1E293B',
      surface: '#F8FAFC',
      accent: '#64748B',
      success: '#059669',
      warning: '#D97706',
      error: '#E11D48'
    },
    typography: {
      heading: 'Inter',
      body: 'Inter',
      scale: '1'
    },
    density: 'medium' as const,
    componentStyle: {
      radius: '6px',
      shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      border: '1px solid #CBD5E1',
      style: 'filled' as const
    }
  }
} as const;

type ThemeKey = keyof typeof THEMES | 'custom';

interface ThemeConfig {
  theme: ThemeKey;
  tokens: {
    primary: string;
    background: string;
    text: string;
    surface: string;
    accent: string;
    success: string;
    warning: string;
    error: string;
  };
  typography: {
    heading: string;
    body: string;
    scale: string;
  };
  density: 'compact' | 'medium' | 'spacious';
  componentStyle: {
    radius: string;
    shadow: string;
    border: string;
    style: 'filled' | 'outline' | 'ghost';
  };
}

export default function DesignSystem({ profile, lastBuild, setLastBuild }: { profile: any, lastBuild: any, setLastBuild: any }) {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  const [project, setProject] = useState<any>(null);
  const [sourceCode, setSourceCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [viewDevice, setViewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [projectBuilds, setProjectBuilds] = useState<any[]>([]);

  useEffect(() => {
    if (!projectId) return;
    const q = query(
      collection(db, `projects/${projectId}/documents`),
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    return onSnapshot(q, (snapshot) => {
      const builds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjectBuilds(builds);
    });
  }, [projectId]);

  const handleSelectBuild = (build: any) => {
    const files = parseFiles(build.content);
    setLastBuild({
      brief: build.brief || project?.name,
      files: Object.entries(files).map(([name, content]: [string, any]) => ({ name, content })),
      docId: build.id
    });
    toast.success(`Loaded build: ${build.id.slice(0, 8)}`);
  };
  
  const [selectedElement, setSelectedElement] = useState<VisualElement | null>(null);
  const [editableContent, setEditableContent] = useState('');
  
  // Design Config Refactor
  const [config, setConfig] = useState<ThemeConfig>({
    theme: 'modern-ai',
    tokens: THEMES['modern-ai'].tokens,
    typography: THEMES['modern-ai'].typography,
    density: THEMES['modern-ai'].density,
    componentStyle: THEMES['modern-ai'].componentStyle as any
  });

  const [customizeExpanded, setCustomizeExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'pages' | 'styles' | 'assets'>('styles');

  const applyTheme = (themeKey: keyof typeof THEMES) => {
    const theme = THEMES[themeKey];
    setConfig({
      theme: themeKey,
      tokens: { ...theme.tokens },
      typography: { ...theme.typography },
      density: theme.density,
      componentStyle: { ...theme.componentStyle }
    });
    
    // Immediate feedback via iframe postMessage
    iframeRef.current?.contentWindow?.postMessage({
      type: 'YACHT_APPLY_GLOBAL_STYLES',
      theme: themeKey,
      ...theme
    }, '*');

    trackUserAction(auth.currentUser?.uid || 'anon', 'theme_selected', `Selected theme: ${themeKey}`, 'Design', projectId || undefined);
  };

  const updateToken = (key: keyof typeof config.tokens, value: string) => {
    setConfig(prev => ({
      ...prev,
      theme: 'custom',
      tokens: { ...prev.tokens, [key]: value }
    }));
    
    // Live update
    iframeRef.current?.contentWindow?.postMessage({
      type: 'YACHT_APPLY_GLOBAL_STYLES',
      colors: { [key]: value }
    }, '*');
  };

  const updateTypography = (key: keyof typeof config.typography, value: string) => {
    setConfig(prev => ({
      ...prev,
      theme: 'custom',
      typography: { ...prev.typography, [key]: value }
    }));

    iframeRef.current?.contentWindow?.postMessage({
      type: 'YACHT_APPLY_GLOBAL_STYLES',
      fonts: { [key]: value }
    }, '*');
  };

  const [historyIndex, setHistoryIndex] = useState(-1);
  const [historyStack, setHistoryStack] = useState<string[]>([]);

  useEffect(() => {
    if (lastBuild?.files?.length) {
      const indexFile = lastBuild.files.find((f: any) => f.name.toLowerCase() === 'index.html') || lastBuild.files[0];
      const code = indexFile.content;
      // Inject selection script
      const injectedCode = code.replace('</body>', `<script>${SELECTION_SCRIPT}</script></body>`);
      if (injectedCode !== sourceCode) {
        setSourceCode(injectedCode);
      }
    }
  }, [lastBuild]);

  useEffect(() => {
    if (sourceCode && historyIndex === -1) {
      setHistoryStack([sourceCode]);
      setHistoryIndex(0);
    }
  }, [sourceCode, historyIndex]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prev = historyIndex - 1;
      setHistoryIndex(prev);
      setSourceCode(historyStack[prev]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < historyStack.length - 1) {
      const next = historyIndex + 1;
      setHistoryIndex(next);
      setSourceCode(historyStack[next]);
    }
  };

  const recordChange = (newCode: string) => {
    const newStack = historyStack.slice(0, historyIndex + 1);
    newStack.push(newCode);
    if (newStack.length > 30) newStack.shift();
    setHistoryStack(newStack);
    setHistoryIndex(newStack.length - 1);
  };

  useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(doc(db, 'projects', projectId), async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setProject({ id: snapshot.id, ...data });
        
        if (data.designConfig) {
          // Normalization check for legacy format
          if (data.designConfig.colors && !data.designConfig.tokens) {
            setConfig({
              theme: 'custom',
              tokens: {
                ...THEMES['modern-ai'].tokens, // fallback defaults
                primary: data.designConfig.colors.primary,
                background: data.designConfig.colors.background,
                text: data.designConfig.colors.text,
                surface: data.designConfig.colors.surface,
                accent: data.designConfig.colors.accent,
                success: data.designConfig.colors.success,
                warning: data.designConfig.colors.warning,
                error: data.designConfig.colors.error
              },
              typography: {
                heading: data.designConfig.fonts.heading,
                body: data.designConfig.fonts.body,
                scale: data.designConfig.fonts.scale || '1'
              },
              density: 'medium',
              componentStyle: {
                radius: data.designConfig.styles.radius || '8px',
                shadow: 'none',
                border: 'none',
                style: data.designConfig.styles.buttonStyle || 'filled'
              }
            });
          } else {
            setConfig(data.designConfig);
          }
        }

        if (!sourceCode) {
          let code = '';
          if (data.architectState?.sourceCode) {
            code = data.architectState.sourceCode;
          } else if (data.activeBuildId) {
            const buildSnap = await getDoc(doc(db, `projects/${projectId}/documents`, data.activeBuildId));
            if (buildSnap.exists()) {
              code = (buildSnap.data() as any).content;
            }
          }
          if (code) {
             const files = parseFiles(code);
             const targetCode = files['index.html'] || code;
             
             // Inject script into code
             const injectedCode = targetCode.replace('</body>', `<script>${SELECTION_SCRIPT}</script></body>`);
             setSourceCode(injectedCode);
          }
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [projectId]);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data.type === 'YACHT_ELEMENT_SELECTED') {
        setSelectedElement(e.data);
        setEditableContent(e.data.innerHTML);
      }
      if (e.data.type === 'YACHT_HTML_OUTPUT') {
        saveHtmlToDb(e.data.html);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [projectId]);

  const saveHtmlToDb = async (html: string) => {
    if (!projectId) return;
    setIsSaving(true);
    try {
      // Remove our injected script before saving
      const cleanHtml = html.replace(/<script>window\.__YACHT_EDITOR_INITIALIZED__[\s\S]*?<\/script>/, '');
      
      await updateDoc(doc(db, 'projects', projectId), {
        'architectState.sourceCode': cleanHtml,
        designConfig: config,
        updatedAt: serverTimestamp()
      });
      toast.success("Design iterations synchronized.");
    } catch (error) {
      toast.error("Synchronization failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const syncChanges = () => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'YACHT_GET_HTML' }, '*');
  };

  const updateElementContent = (newContent: string) => {
    setEditableContent(newContent);
    iframeRef.current?.contentWindow?.postMessage({
      type: 'YACHT_UPDATE_ELEMENT',
      content: newContent
    }, '*');
  };

  const updateElementStyles = (styles: any) => {
    iframeRef.current?.contentWindow?.postMessage({
      type: 'YACHT_UPDATE_ELEMENT',
      styles
    }, '*');
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Constraints
    const isLogo = selectedElement?.tagName === 'IMG' && (selectedElement?.className.includes('logo') || selectedElement?.id.includes('logo'));
    const maxSize = isLogo ? 2 * 1024 * 1024 : 5 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast.error(`File too large. Max size is ${isLogo ? '2MB' : '5MB'}`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const src = event.target?.result as string;
      iframeRef.current?.contentWindow?.postMessage({
        type: 'YACHT_UPDATE_ELEMENT',
        src
      }, '*');
      if (selectedElement) {
        setSelectedElement({ ...selectedElement, src });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRepublish = async () => {
    if (!projectId || !sourceCode) return;
    setIsPublishing(true);
    const toastId = toast.loading("Synthesizing release for Ship engine...");
    try {
      // Trigger HTML extraction
      iframeRef.current?.contentWindow?.postMessage({ type: 'YACHT_GET_HTML' }, '*');
      
      // Wait for the message return via the saveHtmlToDb which will also save it.
      // But for republish, we want to hit the deployments collection.
      // We'll delay slightly to ensure save finishes or do it explicitly here if we have the cleaned code.
      
      await addDoc(collection(db, 'deployments'), {
        projectId,
        ownerId: auth.currentUser?.uid,
        title: `${project?.name} - Custom Refinement`,
        content: sourceCode.replace(/<script>window\.__YACHT_EDITOR_INITIALIZED__[\s\S]*?<\/script>/, ''),
        status: 'Live',
        deployedAt: serverTimestamp()
      });
      toast.success("Build pushed to Ship engine.");
      setTimeout(() => {
        navigate(`/studio/ship?project=${projectId}`);
      }, 1500);
    } catch (error) {
      toast.error("Republish generation failed.");
    } finally {
      setIsPublishing(false);
      toast.dismiss(toastId);
    }
  };

  if (!projectId) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 font-mono text-xs uppercase tracking-widest bg-black">
        Initialize project context to activate Design Identity
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-black text-white font-sans selection:bg-[#0074D9]">
      {/* Top Bar */}
      <header className="h-16 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-xl px-8 flex items-center justify-between z-50">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
             <div>
               <h1 className="text-sm font-mono uppercase tracking-widest text-white leading-none">Design Lab</h1>
               <p className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest mt-1">{project?.name || 'Loading Project...'}</p>
             </div>
          </div>
          <div className="h-6 w-px bg-zinc-900" />
          <div className="flex items-center bg-zinc-900 rounded-lg p-1 border border-zinc-800">
            <button onClick={() => setViewDevice('desktop')} className={`p-2 rounded transition-all ${viewDevice === 'desktop' ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}><Monitor size={14} /></button>
            <button onClick={() => setViewDevice('tablet')} className={`p-2 rounded transition-all ${viewDevice === 'tablet' ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}><Tablet size={14} /></button>
            <button onClick={() => setViewDevice('mobile')} className={`p-2 rounded transition-all ${viewDevice === 'mobile' ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}><Smartphone size={14} /></button>
          </div>
          <div className="h-6 w-px bg-zinc-900" />
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">Build Cache:</span>
            <select 
              onChange={(e) => {
                const b = projectBuilds.find(build => build.id === e.target.value);
                if (b) handleSelectBuild(b);
              }}
              value={lastBuild?.docId || ''}
              className="bg-zinc-900 border border-zinc-800 text-[10px] font-mono text-white px-3 py-1 rounded-lg focus:outline-none focus:border-[#0074D9]"
            >
              <option value="" disabled>Select Version</option>
              {projectBuilds.map(b => (
                <option key={b.id} value={b.id}>
                  {b.id.slice(0, 8)} - {new Date(b.createdAt?.seconds * 1000).toLocaleTimeString()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 mr-4">
             <button className="p-2 text-zinc-600 hover:text-white transition-colors"><Undo2 size={16} /></button>
             <button className="p-2 text-zinc-600 hover:text-white transition-colors"><Redo2 size={16} /></button>
          </div>
          <button 
            onClick={syncChanges}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono font-bold uppercase tracking-widest text-white hover:bg-zinc-800 transition-all disabled:opacity-50"
          >
            {isSaving ? <RefreshCw className="animate-spin" size={12} /> : <Save size={12} />}
            Sync
          </button>
          <button 
            onClick={handleRepublish}
            disabled={isPublishing}
            className="px-6 py-2 bg-[#0074D9] rounded-lg text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white hover:bg-[#0074D9]/80 shadow-[0_0_15px_rgba(0,116,217,0.3)] transition-all disabled:opacity-50"
          >
            Republish
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Navigation & Global Styles */}
        <aside className="w-80 border-r border-zinc-900 bg-zinc-950 flex flex-col overflow-hidden">
          <div className="flex border-b border-zinc-900 bg-black/40">
             {[
               { id: 'pages', icon: Layout, label: 'Tree' },
               { id: 'styles', icon: Palette, label: 'Brand' },
               { id: 'assets', icon: ImageIcon, label: 'Media' }
             ].map(t => (
               <button 
                 key={t.id}
                 onClick={() => setActiveTab(t.id as any)}
                 className={`flex-1 py-4 flex flex-col items-center gap-1 transition-all ${activeTab === t.id ? 'bg-zinc-900 text-[#0074D9] border-b-2 border-[#0074D9]' : 'text-zinc-600 hover:text-zinc-400'}`}
               >
                 <t.icon size={14} />
                 <span className="text-[8px] font-mono uppercase tracking-widest">{t.label}</span>
               </button>
             ))}
          </div>

          <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
            {activeTab === 'styles' && (
              <div className="space-y-8 animate-in fade-in duration-500">
                {/* Theme Selector Section */}
                <section className="space-y-4">
                  <label className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest flex items-center justify-between">
                    <span>Identity Presets</span>
                    <span className="text-[8px] opacity-70">Project Mode: {config.theme}</span>
                  </label>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {(Object.entries(THEMES) as [keyof typeof THEMES, typeof THEMES['modern-ai']][]).map(([key, theme]) => (
                      <button
                        key={key}
                        onClick={() => applyTheme(key)}
                        className={`group relative flex flex-col text-left border rounded-xl overflow-hidden transition-all duration-300 ${
                          config.theme === key 
                            ? 'border-[#0074D9] ring-1 ring-[#0074D9] bg-[#0074D9]/5' 
                            : 'border-zinc-900 bg-zinc-900/20 hover:border-zinc-700 hover:bg-zinc-900/40'
                        }`}
                      >
                        {/* Visual Preview Thumbnail */}
                        <div className={`h-16 w-full relative overflow-hidden flex items-center justify-center`} style={{ backgroundColor: theme.tokens.background }}>
                          {key === 'modern-ai' && (
                             <div className="relative">
                               <div className="w-12 h-12 rounded-full blur-xl opacity-40 bg-[#00F2FF] animate-pulse" />
                               <div className="absolute inset-0 flex items-center justify-center">
                                 <Zap size={16} className="text-[#00F2FF]" />
                               </div>
                             </div>
                          )}
                          {key === 'workhorse' && (
                             <div className="w-full px-2 space-y-1">
                               <div className="h-1 w-full bg-zinc-200/20 rounded-full" />
                               <div className="h-1 w-2/3 bg-zinc-200/20 rounded-full" />
                               <div className="h-3 w-1/4 bg-[#2563EB] rounded-sm mt-1" />
                             </div>
                          )}
                          {key === 'brutalist' && (
                             <div className="border-2 border-black p-1 flex gap-1">
                               <div className="w-4 h-4 bg-black" />
                               <div className="w-4 h-4 bg-[#FFFF00] border border-black" />
                             </div>
                          )}
                          {key === 'editorial' && (
                             <div className="text-center font-serif text-[#1A1A1A]">
                               <p className="text-[8px] italic opacity-60">Vol. 1</p>
                               <p className="text-xs uppercase tracking-widest font-bold">Aura</p>
                             </div>
                          )}
                          {key === 'playful' && (
                             <div className="relative">
                               <div className="w-6 h-6 rounded-full bg-[#FF4D94] shadow-lg" />
                               <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#7C3AED]" />
                             </div>
                          )}
                          {key === 'enterprise' && (
                             <div className="grid grid-cols-3 gap-0.5 opacity-40">
                               {Array.from({length: 9}).map((_, i) => (
                                 <div key={i} className="w-3 h-3 bg-zinc-400" />
                               ))}
                             </div>
                          )}
                        </div>

                        <div className="p-3 space-y-1">
                          <h4 className="text-[10px] font-mono font-bold uppercase tracking-wider text-white">
                            {theme.name}
                          </h4>
                          <p className="text-[8px] text-zinc-500 font-sans leading-tight line-clamp-2">
                            {theme.description}
                          </p>
                        </div>

                        {config.theme === key && (
                          <div className="absolute top-2 right-2">
                            <div className="bg-[#0074D9] rounded-full p-0.5">
                              <Check size={8} className="text-white" />
                            </div>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="pt-4">
                  <button 
                    onClick={() => setCustomizeExpanded(!customizeExpanded)}
                    className="w-full flex items-center justify-between py-3 px-4 bg-zinc-900/50 border border-zinc-800 rounded-xl group hover:border-zinc-700 transition-all"
                  >
                    <div className="flex items-center gap-2 text-zinc-400 group-hover:text-white transition-colors">
                      <Settings2 size={14} />
                      <span className="text-[10px] font-mono uppercase tracking-widest">Advanced Customization</span>
                    </div>
                    <ChevronRight size={14} className={`text-zinc-600 transition-transform duration-300 ${customizeExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {customizeExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-8 pt-8 px-1">
                          <div className="space-y-4">
                             <label className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest flex items-center justify-between">
                               <span>Primitive Tokens</span>
                               <button 
                                 onClick={() => config.theme !== 'custom' && applyTheme(config.theme as any)}
                                 className="text-[#0074D9] hover:underline"
                               >
                                 Reset
                               </button>
                             </label>
                             <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                               {Object.entries(config.tokens).map(([key, value]) => (
                                 <div key={key} className="space-y-1.5">
                                   <span className="text-[8px] font-mono uppercase text-zinc-500">{key}</span>
                                   <div className="flex items-center gap-2 group">
                                     <input 
                                         type="color" 
                                         value={value}
                                         onChange={(e) => updateToken(key as any, e.target.value)}
                                         className="w-5 h-5 rounded-sm border border-zinc-800 cursor-pointer bg-transparent"
                                      />
                                      <input 
                                         type="text" 
                                         value={value}
                                         onChange={(e) => updateToken(key as any, e.target.value)}
                                         className="bg-zinc-900 border border-zinc-800 text-[9px] font-mono uppercase p-1 w-full text-zinc-400 rounded focus:border-[#0074D9] outline-none"
                                      />
                                   </div>
                                 </div>
                               ))}
                             </div>
                          </div>

                          <div className="space-y-4 pt-8 border-t border-zinc-900">
                            <label className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">Typography Matrix</label>
                            <div className="space-y-4">
                               <div className="space-y-2">
                                 <span className="text-[8px] font-mono uppercase text-zinc-500">Heading Definition</span>
                                 <select 
                                   value={config.typography.heading}
                                   onChange={(e) => updateTypography('heading', e.target.value)}
                                   className="w-full bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono uppercase p-3 text-white focus:ring-1 focus:ring-[#0074D9] outline-none"
                                 >
                                    <option>Inter</option>
                                    <option>Space Grotesk</option>
                                    <option>JetBrains Mono</option>
                                    <option>Outfit</option>
                                    <option>Playfair Display</option>
                                 </select>
                               </div>
                               <div className="space-y-2">
                                 <span className="text-[8px] font-mono uppercase text-zinc-500">Body Definition</span>
                                 <select 
                                   value={config.typography.body}
                                   onChange={(e) => updateTypography('body', e.target.value)}
                                   className="w-full bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono uppercase p-3 text-white focus:ring-1 focus:ring-[#0074D9] outline-none"
                                 >
                                    <option>Inter</option>
                                    <option>Outfit</option>
                                    <option>Roboto</option>
                                    <option>Geist</option>
                                    <option>PT Serif</option>
                                 </select>
                               </div>
                            </div>
                          </div>

                          <div className="space-y-4 pt-8 border-t border-zinc-900">
                            <label className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">Density & Style</label>
                            <div className="space-y-6">
                              <div className="space-y-2">
                                <span className="text-[8px] font-mono uppercase text-zinc-500">Layout Density</span>
                                <div className="grid grid-cols-3 gap-2">
                                   {['compact', 'medium', 'spacious'].map(d => (
                                     <button 
                                       key={d}
                                       onClick={() => setConfig({...config, theme: 'custom', density: d as any})}
                                       className={`py-1.5 rounded text-[8px] font-mono uppercase border transition-all ${config.density === d ? 'bg-[#0074D9] border-[#0074D9] text-white' : 'bg-transparent border-zinc-800 text-zinc-500 hover:text-white'}`}
                                     >
                                       {d}
                                     </button>
                                   ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-[8px] font-mono uppercase text-zinc-500">Corner Radius</span>
                                  <span className="text-[10px] font-mono text-white">{config.componentStyle.radius}</span>
                                </div>
                                <input 
                                  type="range" min="0" max="36" step="2"
                                  value={parseInt(config.componentStyle.radius)}
                                  onChange={(e) => setConfig({ ...config, theme: 'custom', componentStyle: { ...config.componentStyle, radius: `${e.target.value}px` } })}
                                  className="w-full h-1 bg-zinc-800 accent-[#0074D9] rounded-lg cursor-pointer" 
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </section>
              </div>
            )}

            {activeTab === 'pages' && (
              <div className="space-y-6 animate-in slide-in-from-left-4 duration-300">
                <div className="p-4 bg-zinc-900/40 border border-zinc-800 rounded-xl space-y-3">
                  <div className="flex items-center gap-2 text-[#0074D9]">
                    <Check size={14} />
                    <span className="text-[10px] font-mono uppercase font-bold">Index Prototype</span>
                  </div>
                  <div className="space-y-2 pl-4">
                     {['Navigation', 'Hero Section', 'Features Grid', 'Pricing Plan', 'Testimonials', 'Footer'].map(n => (
                       <div key={n} className="flex items-center gap-3 text-zinc-600 group cursor-pointer hover:text-zinc-300">
                          <Layers size={10} className="group-hover:text-[#0074D9]" />
                          <span className="text-[9px] font-mono uppercase">{n}</span>
                       </div>
                     ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'assets' && (
               <div className="space-y-6 animate-in slide-in-from-left-4 duration-300">
                  <div className="flex flex-col gap-4">
                    <div className="p-8 border border-dashed border-zinc-800 rounded-2xl text-center space-y-3 group hover:border-zinc-700 transition-all cursor-pointer">
                       <div className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center mx-auto text-zinc-600 group-hover:text-[#0074D9] transition-all">
                          <Upload size={18} />
                       </div>
                       <div>
                          <p className="text-[10px] font-mono uppercase text-zinc-500">Global Upload</p>
                          <p className="text-[8px] font-mono text-zinc-700 mt-1 uppercase">Drop assets to use in Customizer</p>
                       </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                       <div className="aspect-square bg-zinc-900 border border-zinc-800 rounded-lg animate-pulse" />
                       <div className="aspect-square bg-zinc-900 border border-zinc-800 rounded-lg animate-pulse" />
                    </div>
                  </div>
               </div>
            )}
          </div>
        </aside>

        {/* Center: Live App Preview */}
        <main className="flex-1 bg-[#050505] flex items-center justify-center p-8 relative overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center gap-4">
              <RefreshCw className="animate-spin text-[#0074D9]" size={32} />
              <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Rendering Design Stage...</p>
            </div>
          ) : (
            <div className={`h-full transition-all duration-500 shadow-2xl bg-white overflow-hidden rounded-lg ${viewDevice === 'desktop' ? 'w-full' : viewDevice === 'tablet' ? 'w-[768px]' : 'w-[420px]'}`}>
              <iframe 
                ref={iframeRef}
                srcDoc={sourceCode}
                title="Design Stage"
                className="w-full h-full border-none"
                sandbox="allow-scripts allow-forms allow-popups"
                onLoad={() => {
                  iframeRef.current?.contentWindow?.postMessage({
                    type: 'YACHT_APPLY_GLOBAL_STYLES',
                    ...config
                  }, '*');
                }}
              />
            </div>
          )}
        </main>

        {/* Right Panel: Contextual Inspector */}
        <aside className="w-85 border-l border-zinc-900 bg-zinc-950 flex flex-col overflow-y-auto">
          <div className="p-6">
            {!selectedElement ? (
              <div className="h-full flex flex-col items-center justify-center py-20 text-center space-y-4 opacity-40">
                <MousePointer2 size={32} className="text-zinc-600" />
                <div>
                  <h4 className="text-[10px] font-mono uppercase tracking-widest text-white">Select Component</h4>
                  <p className="text-[9px] text-zinc-500 font-sans mt-1">Pick any live element on the stage to modify its contents.</p>
                </div>
              </div>
            ) : (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-8"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="px-2 py-1 bg-[#111111] border border-[#222222] rounded flex items-center gap-2">
                       <span className="text-[10px] font-mono uppercase text-[#0074D9] font-bold">{selectedElement.tagName}</span>
                    </div>
                  </div>
                  <button onClick={() => setSelectedElement(null)} className="text-zinc-600 hover:text-white"><X size={16} /></button>
                </div>

                <section className="space-y-4">
                  <label className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest flex items-center gap-2">
                    <TypeIcon size={10} /> Content Strategy
                  </label>
                  {selectedElement.tagName === 'IMG' ? (
                    <div className="space-y-4">
                       <div className="aspect-video bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800 flex items-center justify-center">
                          <img src={selectedElement.src} className="max-w-full max-h-full object-contain" />
                       </div>
                       <label className="block">
                          <div className="w-full py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-[10px] font-mono font-bold uppercase tracking-widest text-white hover:bg-zinc-800 transition-all flex items-center justify-center gap-2 cursor-pointer">
                             <Upload size={14} /> Replace Asset
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                       </label>
                       <p className="text-[8px] font-mono text-zinc-600 uppercase text-center tracking-tighter">Accepted: PNG, JPG, WEBP. Max 5MB</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <textarea 
                        value={editableContent}
                        onChange={(e) => updateElementContent(e.target.value)}
                        className="w-full bg-black border border-zinc-900 rounded-xl p-4 text-xs text-white focus:outline-none focus:border-[#0074D9] placeholder:text-zinc-700 resize-none font-sans min-h-[120px]"
                        placeholder="Content source..."
                      />
                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-2">
                            <span className="text-[8px] font-mono uppercase text-zinc-500">Text Color</span>
                            <input 
                              type="color" 
                              value={selectedElement.style.color}
                              onChange={(e) => updateElementStyles({ color: e.target.value })}
                              className="w-full h-8 bg-black border border-zinc-900 rounded cursor-pointer" 
                            />
                         </div>
                         <div className="space-y-2">
                            <span className="text-[8px] font-mono uppercase text-zinc-500">Font Size</span>
                            <div className="flex items-center gap-2 bg-black border border-zinc-900 rounded px-2">
                               <input 
                                 type="text" 
                                 value={selectedElement.style.fontSize}
                                 className="bg-transparent text-[10px] font-mono text-white p-1.5 w-full outline-none"
                               />
                            </div>
                         </div>
                      </div>
                    </div>
                  )}
                </section>

                <section className="space-y-4 pt-8 border-t border-zinc-900">
                  <label className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest flex items-center gap-2">
                    <Settings2 size={10} /> Primitive Logic
                  </label>
                  <div className="space-y-4">
                     <div className="flex justify-between items-center bg-zinc-900/30 p-3 rounded-lg border border-zinc-900">
                        <span className="text-[9px] font-mono uppercase text-zinc-400">Class Matrix</span>
                        <span className="text-[8px] font-mono text-[#0074D9] truncate max-w-[120px]">{selectedElement.className || 'none'}</span>
                     </div>
                     <button className="w-full py-2 bg-zinc-900 border border-zinc-800 rounded text-[9px] font-mono uppercase text-zinc-500 hover:text-white transition-all">Reset to Blueprint</button>
                  </div>
                </section>
              </motion.div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
